import { useQueryClient } from "@tanstack/react-query";
import { FC, FormEvent, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { OrganizationRole } from "~/api/model";
import {
  getGetOrganizationSettingsQueryKey,
  useGetOrganizationSettings,
  useUpdateOrganizationSettings,
} from "~/api/organizations/organizations";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { SaveButton, SaveButtonRef } from "~/components/SaveButton";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { BatesStampPositionPicker } from "~/features/settings/components/BatesStampPositionPicker";
import { useOrganizationParams } from "~/helpers";
import { useTrackPageVisit } from "~/hooks/useTrackPageVisit";
import {
  BATES_AFFIX_MAX_LENGTH,
  BATES_DEFAULT_DIGITS,
  BATES_DEFAULT_PREFIX_SOURCE,
  BATES_DEFAULT_STAMP_COLOR,
  BATES_DEFAULT_STAMP_POSITION,
  BATES_DEFAULT_START_NUMBER,
  BATES_DEFAULT_SUFFIX_SOURCE,
  BATES_MAX_DIGITS,
  BATES_MIN_DIGITS,
  formatBatesPreview,
  isBatesPrefixSource,
  isBatesStampPosition,
  isBatesSuffixSource,
  type BatesPrefixSource,
  type BatesStampPosition,
  type BatesSuffixSource,
  type OrganizationSettingsWithBates,
} from "~/types/bates";
import { trackEvent } from "~/utils/analytics";

export const SettingsBatesNumbering: FC = () => {
  useTrackPageVisit("app/settings_bates_numbering/visit");

  const intl = useIntl();
  const { organizationId } = useOrganizationParams();
  const { data: organizationSettings } =
    useGetOrganizationSettings(organizationId);
  const { mutate: updateSettings, isPending } = useUpdateOrganizationSettings();
  const saveButtonRef = useRef<SaveButtonRef>(null);
  const queryClient = useQueryClient();

  const [batesPrefixSource, setBatesPrefixSource] = useState<BatesPrefixSource>(
    BATES_DEFAULT_PREFIX_SOURCE
  );
  const [batesPrefix, setBatesPrefix] = useState("");
  const [batesSuffixSource, setBatesSuffixSource] = useState<BatesSuffixSource>(
    BATES_DEFAULT_SUFFIX_SOURCE
  );
  const [batesSuffix, setBatesSuffix] = useState("");
  const [batesStartNumber, setBatesStartNumber] = useState(
    BATES_DEFAULT_START_NUMBER
  );
  const [batesDigits, setBatesDigits] = useState(BATES_DEFAULT_DIGITS);
  const [batesStampColor, setBatesStampColor] = useState(
    BATES_DEFAULT_STAMP_COLOR
  );
  const [batesStampPosition, setBatesStampPosition] =
    useState<BatesStampPosition>(BATES_DEFAULT_STAMP_POSITION);

  useEffect(() => {
    const settings = organizationSettings as
      | OrganizationSettingsWithBates
      | undefined;
    if (!settings) return;

    setBatesPrefixSource(
      isBatesPrefixSource(settings.batesPrefixSource)
        ? settings.batesPrefixSource
        : BATES_DEFAULT_PREFIX_SOURCE
    );
    setBatesPrefix(settings.batesPrefix ?? "");
    setBatesSuffixSource(
      isBatesSuffixSource(settings.batesSuffixSource)
        ? settings.batesSuffixSource
        : BATES_DEFAULT_SUFFIX_SOURCE
    );
    setBatesSuffix(settings.batesSuffix ?? "");
    setBatesStartNumber(
      settings.batesStartNumber ?? BATES_DEFAULT_START_NUMBER
    );
    setBatesDigits(settings.batesDigits ?? BATES_DEFAULT_DIGITS);
    setBatesStampColor(settings.batesStampColor ?? BATES_DEFAULT_STAMP_COLOR);
    setBatesStampPosition(
      isBatesStampPosition(settings.batesStampPosition)
        ? settings.batesStampPosition
        : BATES_DEFAULT_STAMP_POSITION
    );
  }, [organizationSettings]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();

    trackEvent({
      eventName: "app/settings_bates_numbering/save",
      properties: {
        prefixSource: batesPrefixSource,
        suffixSource: batesSuffixSource,
        stampColor: batesStampColor,
        stampPosition: batesStampPosition,
      },
    });

    updateSettings(
      {
        organizationId,
        data: {
          batesPrefixSource,
          batesPrefix: batesPrefix.trim() || null,
          batesSuffixSource,
          batesSuffix: batesSuffix.trim() || null,
          batesStartNumber,
          batesDigits,
          batesStampColor,
          batesStampPosition,
        } as Parameters<typeof updateSettings>[0]["data"],
      },
      {
        onSuccess: (updatedSettings) => {
          saveButtonRef.current?.onSuccess();
          queryClient.setQueryData(
            getGetOrganizationSettingsQueryKey(organizationId),
            updatedSettings
          );
        },
      }
    );
  };

  const handlePrefixSourceChange = (value: string) => {
    if (!isBatesPrefixSource(value)) return;
    setBatesPrefixSource(value);
    if (value === "FILE_NAME" && batesSuffixSource === "FILE_NAME") {
      setBatesSuffixSource("NONE");
    }
  };

  const handleSuffixSourceChange = (value: string) => {
    if (!isBatesSuffixSource(value)) return;
    setBatesSuffixSource(value);
    if (value === "FILE_NAME" && batesPrefixSource === "FILE_NAME") {
      setBatesPrefixSource("CASE_NAME");
    }
  };

  const previewPrefix =
    batesPrefixSource === "CUSTOM"
      ? batesPrefix
      : intl.formatMessage(
          batesPrefixSource === "FILE_NAME"
            ? {
                id: "settings.batesNumbering.previewFileNamePrefix",
                defaultMessage: "FileName",
              }
            : {
                id: "settings.batesNumbering.previewCaseNamePrefix",
                defaultMessage: "CaseName",
              }
        );

  const previewSuffix =
    batesSuffixSource === "CUSTOM"
      ? batesSuffix
      : batesSuffixSource === "FILE_NAME"
        ? intl.formatMessage({
            id: "settings.batesNumbering.previewFileNameSuffix",
            defaultMessage: "FileName",
          })
        : "";

  const previewText = formatBatesPreview({
    prefix: previewPrefix,
    suffix: previewSuffix,
    startNumber: batesStartNumber,
    digits: batesDigits,
  });

  return (
    <DisplayFor roles={[OrganizationRole.OWNER, OrganizationRole.SUPERADMIN]}>
      <div className="flex flex-col lg:flex-row gap-6 lg:gap-14 w-full">
        <form
          onSubmit={onSubmit}
          className="flex flex-col gap-6 mt-2 w-full lg:w-[60%]"
        >
          <div>
            <h3 className="text-2xl font-semibold">
              <FormattedMessage
                id="settings.batesNumbering.title"
                defaultMessage="Bates Numbering"
              />
            </h3>
            <p className="text-sm text-muted-foreground mt-1">
              <FormattedMessage
                id="settings.batesNumbering.description"
                defaultMessage="Configure the default format for Bates number stamps applied to documents."
              />
            </p>
          </div>

          {/* Prefix & Suffix Sources */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="batesPrefixSource">
                <FormattedMessage
                  id="settings.batesNumbering.prefixSourceLabel"
                  defaultMessage="Default Prefix"
                />
              </Label>
              <Select
                value={batesPrefixSource}
                onValueChange={handlePrefixSourceChange}
              >
                <SelectTrigger id="batesPrefixSource">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CASE_NAME">
                    <FormattedMessage
                      id="settings.batesNumbering.prefixSourceCaseName"
                      defaultMessage="Auto-Generated from Case Name"
                    />
                  </SelectItem>
                  <SelectItem value="FILE_NAME">
                    <FormattedMessage
                      id="settings.batesNumbering.prefixSourceFileName"
                      defaultMessage="Document File Name"
                    />
                  </SelectItem>
                  <SelectItem value="CUSTOM">
                    <FormattedMessage
                      id="settings.batesNumbering.prefixSourceCustom"
                      defaultMessage="Custom Text"
                    />
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {batesPrefixSource === "CUSTOM" && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="batesPrefix">
                  <FormattedMessage
                    id="settings.batesNumbering.prefixLabel"
                    defaultMessage="Custom Prefix"
                  />
                </Label>
                <Input
                  id="batesPrefix"
                  value={batesPrefix}
                  maxLength={BATES_AFFIX_MAX_LENGTH}
                  placeholder={intl.formatMessage({
                    id: "settings.batesNumbering.prefixPlaceholder",
                    defaultMessage: "e.g. SMITH",
                  })}
                  onChange={(e) => setBatesPrefix(e.target.value)}
                />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <Label htmlFor="batesSuffixSource">
                <FormattedMessage
                  id="settings.batesNumbering.suffixSourceLabel"
                  defaultMessage="Default Suffix"
                />
              </Label>
              <Select
                value={batesSuffixSource}
                onValueChange={handleSuffixSourceChange}
              >
                <SelectTrigger id="batesSuffixSource">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NONE">
                    <FormattedMessage
                      id="settings.batesNumbering.suffixSourceNone"
                      defaultMessage="None"
                    />
                  </SelectItem>
                  <SelectItem
                    value="FILE_NAME"
                    disabled={batesPrefixSource === "FILE_NAME"}
                  >
                    <FormattedMessage
                      id="settings.batesNumbering.suffixSourceFileName"
                      defaultMessage="Document File Name"
                    />
                  </SelectItem>
                  <SelectItem value="CUSTOM">
                    <FormattedMessage
                      id="settings.batesNumbering.suffixSourceCustom"
                      defaultMessage="Custom Text"
                    />
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {batesSuffixSource === "CUSTOM" && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="batesSuffix">
                  <FormattedMessage
                    id="settings.batesNumbering.suffixLabel"
                    defaultMessage="Custom Suffix"
                  />
                </Label>
                <Input
                  id="batesSuffix"
                  value={batesSuffix}
                  maxLength={BATES_AFFIX_MAX_LENGTH}
                  placeholder={intl.formatMessage({
                    id: "settings.batesNumbering.suffixPlaceholder",
                    defaultMessage: "e.g. CONF",
                  })}
                  onChange={(e) => setBatesSuffix(e.target.value)}
                />
              </div>
            )}
          </div>

          {/* Number Format */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="batesStartNumber">
                <FormattedMessage
                  id="settings.batesNumbering.startNumberLabel"
                  defaultMessage="Start Numbering With"
                />
              </Label>
              <Input
                id="batesStartNumber"
                type="number"
                min={BATES_DEFAULT_START_NUMBER}
                value={batesStartNumber}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  setBatesStartNumber(
                    Number.isNaN(parsed)
                      ? BATES_DEFAULT_START_NUMBER
                      : Math.max(BATES_DEFAULT_START_NUMBER, parsed)
                  );
                }}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="batesDigits">
                <FormattedMessage
                  id="settings.batesNumbering.digitsLabel"
                  defaultMessage="Number of Digits"
                />
              </Label>
              <Input
                id="batesDigits"
                type="number"
                min={BATES_MIN_DIGITS}
                max={BATES_MAX_DIGITS}
                value={batesDigits}
                onChange={(e) => {
                  const parsed = parseInt(e.target.value, 10);
                  setBatesDigits(
                    Number.isNaN(parsed)
                      ? BATES_DEFAULT_DIGITS
                      : Math.min(
                          BATES_MAX_DIGITS,
                          Math.max(BATES_MIN_DIGITS, parsed)
                        )
                  );
                }}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label>
              <FormattedMessage
                id="settings.batesNumbering.stampLabel"
                defaultMessage="Stamp Position and Color"
              />
            </Label>
            <BatesStampPositionPicker
              position={batesStampPosition}
              color={batesStampColor}
              onPositionChange={setBatesStampPosition}
              onColorChange={setBatesStampColor}
            />
          </div>

          <SaveButton isPending={isPending} ref={saveButtonRef} />
        </form>

        <div className="flex flex-col gap-4 mt-2 w-full lg:w-[60%] lg:max-w-[400px]">
          <div>
            <h4 className="text-lg font-semibold mb-2">
              <FormattedMessage
                id="settings.batesNumbering.previewLabel"
                defaultMessage="Preview"
              />
            </h4>
            <p className="text-sm text-muted-foreground">
              <FormattedMessage
                id="settings.batesNumbering.preview.description"
                defaultMessage="See where the Bates stamp will land on the page"
              />
            </p>
          </div>
          <div className="w-full max-w-[400px] rounded-lg overflow-hidden shadow-sm border border-border">
            <div
              className={`relative bg-white h-[541px] p-6 flex ${STAMP_POSITION_CLASSES[batesStampPosition]}`}
            >
              <div
                className="absolute inset-x-6 top-12 flex flex-col gap-2"
                aria-hidden="true"
              >
                <div className="h-3 w-1/2 rounded bg-gray-200" />
                <div className="h-2 w-full rounded bg-gray-200" />
                <div className="h-2 w-full rounded bg-gray-200" />
                <div className="h-2 w-5/6 rounded bg-gray-200" />
                <div className="h-2 w-2/3 rounded bg-gray-200" />
                <div className="h-3 w-1/3 rounded bg-gray-200 mt-4" />
                <div className="h-2 w-full rounded bg-gray-200" />
                <div className="h-2 w-full rounded bg-gray-200" />
                <div className="h-2 w-3/4 rounded bg-gray-200" />
              </div>
              <span
                className="relative font-mono text-xs whitespace-nowrap"
                style={{ color: batesStampColor }}
                data-testid="batesPreview"
              >
                {previewText}
              </span>
            </div>
          </div>
        </div>
      </div>
    </DisplayFor>
  );
};

const STAMP_POSITION_CLASSES: Record<BatesStampPosition, string> = {
  TOP_LEFT: "items-start justify-start",
  TOP_CENTER: "items-start justify-center",
  TOP_RIGHT: "items-start justify-end",
  BOTTOM_LEFT: "items-end justify-start",
  BOTTOM_CENTER: "items-end justify-center",
  BOTTOM_RIGHT: "items-end justify-end",
};
